import { useTranslation } from "react-i18next";

import { Download } from "@/assets/icons/ds";
import { ProductCardFooterItem } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardFooterItem";
import { DeviceProductCard } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/DeviceProductCard.tsx";
import { getIconForProductCategory } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { SimStatusChip } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/SimStatusChip.tsx";
import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims.ts";
import { useGetTitle } from "@/hooks/useGetTitle.ts";
import { getFlatNameFromDevice } from "@/utils/deviceUtils";
import { getProductIdFromSim, getProductSpeed } from "@/utils/productMatrix";

interface FlatSpeedTileProps {
  device: ExtendedSimCardDetail;
  manageRoute: string;
}

/**
 * Shared by the "home" (router) and "data" (IoT/precharged data-only)
 * product categories — both use the identical icon + download/upload
 * speed-card footer, just different product speeds.
 */
export const FlatSpeedTile = ({ device, manageRoute }: FlatSpeedTileProps) => {
  const { t } = useTranslation(["portal", "translation"]);

  const icon = getIconForProductCategory(device);
  const title = useGetTitle(device.alias);
  const speed = getProductSpeed(getProductIdFromSim(device));
  const mbitMax = t("translation:Common.tariff.mbitMax");

  return (
    <DeviceProductCard
      dataTestid="flat-speed-tile"
      icon={icon}
      title={title}
      subtitle={getFlatNameFromDevice(device)}
      navigationTarget={`${manageRoute}?simId=${device.simId}`}
      chips={<SimStatusChip status={device.simStatus} />}
      footer={
        <ProductCardFooterItem
          variant="metric-cards"
          cards={[
            {
              // Explicit size: the new ds icon's intrinsic 24x24 viewBox is
              // bigger than the old 20x20 one, and this tile has no wrapper
              // that clamps icon size — pin it to the size it already rendered at.
              icon: <Download className="size-5" />,
              value: `${speed?.download ?? "-"}`,
              unit: mbitMax,
              label: t(
                "translation:Onboarding.sections.rate-selection.download",
              ),
            },
            {
              icon: <Download className="size-5 rotate-180" />,
              value: `${speed?.upload ?? "-"}`,
              unit: mbitMax,
              label: t("translation:Onboarding.sections.rate-selection.upload"),
            },
          ]}
        />
      }
    />
  );
};
